// S8d (2026-06-03) — Block Editor "Dependencies" panel data model.
//
// PURE helper. Reads the selected block + the full block list and
// returns the four rows the Dependencies panel renders:
//
//   • Graph inputs        — block.connections.inputs (edge-based)
//   • Graph outputs       — block.connections.outputs (edge-based)
//   • Runtime refs        — id pointers on block.data (NOT edges):
//                             entry: qtyCalculatorBlockId,
//                                    slCalculatorBlockId,
//                                    tpCalculatorBlockId
//                             expression: leftInputBlockId,
//                                         rightInputBlockId
//                           These are real runtime dependencies that
//                           the canvas edge MAY or MAY NOT mirror.
//   • Referenced by       — reverse scan: every OTHER block that
//                           points at us via the runtime-ref fields
//                           above. Used by, e.g., the qty_calculator
//                           block to show "Long Entry references me
//                           as qtyCalculatorBlockId".
//
// Each row carries enough info for the UI to render:
//   • a compact display string ("RSI < 30 · expression · ...7000a")
//   • the referenced block id (for the navigation callback)
//   • whether the id resolves (unresolved ⇒ non-clickable, "stale")
//
// Engine-behavior-neutral. Does NOT mutate blocks, does NOT touch
// connections, does NOT normalize saved data.

import type { Block } from '../../types';
import { BlockType } from '../../types';

/** Single row in the panel. */
export interface DependencyRef {
  /** Referenced block id. May not resolve to a real block (stale). */
  refId: string;
  /** True iff a block with `refId` exists in the strategy. When false,
   *  the UI renders the row as "stale" / unresolved + non-clickable. */
  resolved: boolean;
  /** Display name of the referenced block (or '(unknown)' if stale).
   *  Pulled from block.data.name with a sensible fallback chain. */
  name: string;
  /** BlockType of the referenced block, or 'unknown' if stale. */
  type: string;
  /** For "Runtime refs" / "Referenced by" rows: which data.* field
   *  carries this pointer (e.g. 'qtyCalculatorBlockId'). Empty string
   *  for plain edge-based rows. */
  via: string;
  /** S8f (2026-06-03) — names of inline data fields on the OWNING
   *  block (the one whose `via` slot carries this pointer) that the
   *  runtime ref displaces at run time. Populated from
   *  RUNTIME_REF_OVERRIDES — e.g. `qtyCalculatorBlockId` overrides
   *  `quantity_type`, `quantity_value`, `risk_per_trade`,
   *  `position_sizing_method`. Empty when the pointer doesn't
   *  shadow any inline fields. The UI uses this to (a) annotate
   *  rows with an "overrides: …" suffix and (b) decide which
   *  inline form fields to render as disabled / "shadowed". */
  overrides: readonly string[];
  /** S8h (2026-06-03) — which channel of the producer's BlockOutput
   *  the CONSUMER reads, when the row is a graph-edge dependency.
   *  Engine model (src/engine/graph.ts): every block output carries
   *  a parallel `values: number[]` and `signals: boolean[]`. Which
   *  track gets consumed depends on the CONSUMER's block type:
   *    'values'    — Expression reads upstream as numeric series.
   *    'signals'   — LogicGate + Entry/Exit/SL/TP terminals read
   *                  upstream as boolean truth-per-bar.
   *    null        — the consumer doesn't read upstream block
   *                  outputs (indicators / volatility / etc.
   *                  evaluate from raw candles + block.data only).
   *                  Edges to these blocks are decorative; the
   *                  engine ignores them.
   *  For Graph-inputs rows, this reflects how the SELECTED block
   *  reads the row's referenced producer. For Graph-outputs rows,
   *  it reflects how the row's referenced consumer reads OUR
   *  output. Runtime-ref rows always carry null (they don't use
   *  the BlockOutput channels — calc blocks are invoked per-trade
   *  with their own ctx/params surface). */
  readsAs: 'values' | 'signals' | null;
}

export interface BlockDependencies {
  graphInputs: DependencyRef[];
  graphOutputs: DependencyRef[];
  runtimeRefs: DependencyRef[];
  referencedBy: DependencyRef[];
}

/** S8h (2026-06-03) — Block-type → which BlockOutput channel the
 *  CONSUMER reads from upstream inputs at run time.
 *
 *  Engine call sites:
 *    'values'  — Expression: src/engine/graph.ts:readOperand case
 *                'input' returns outputs.get(id).values (line 497).
 *    'signals' — LogicGate: evalLogicGate reads o.signals[i]
 *                across all incoming edges (line 643-665).
 *                Terminals: terminalFires reads signals[i] across
 *                all incoming edges (line 751-755).
 *    null      — Indicators (moving_average, momentum_oscillator,
 *                volatility, volume, trend, chart_patterns,
 *                candlestick_patterns, time_filter, price_data,
 *                strategy_config, custom): evaluate from raw
 *                candles + block.data; do NOT read upstream block
 *                outputs. The topo-sort still walks their incoming
 *                edges for ordering, but the evaluator ignores
 *                them. So "Graph inputs" rows on these blocks are
 *                visual hints that don't drive runtime behavior.
 */
const READS_AS: Readonly<Record<string, 'values' | 'signals'>> = Object.freeze({
  [BlockType.EXPRESSION]: 'values',
  [BlockType.LOGIC_GATE]: 'signals',
  [BlockType.ENTRY]: 'signals',
  [BlockType.EXIT]: 'signals',
  [BlockType.STOP_LOSS]: 'signals',
  [BlockType.TAKE_PROFIT]: 'signals',
});

/** Public helper: which channel does this block-type read from its
 *  upstream connections.inputs? Returns null when the type
 *  doesn't consume upstream outputs. */
export function getInputChannel(blockType: BlockType | string): 'values' | 'signals' | null {
  return READS_AS[String(blockType)] ?? null;
}

/** Compute the four rows for the selected block. Pure: same inputs
 *  produce same output; never mutates. */
export function computeBlockDependencies(
  selected: Block,
  allBlocks: Block[],
): BlockDependencies {
  const byId = new Map<string, Block>();
  for (const b of allBlocks) byId.set(b.id, b);

  const resolve = (
    id: string,
    via = '',
    readsAs: 'values' | 'signals' | null = null,
  ): DependencyRef => {
    const overrides = via ? overridesFor(via) : EMPTY;
    const b = byId.get(id);
    if (!b) {
      return {
        refId: id, resolved: false, name: '(unknown)', type: 'unknown',
        via, overrides, readsAs,
      };
    }
    return {
      refId: id,
      resolved: true,
      name: displayName(b),
      type: String(b.type),
      via,
      overrides,
      readsAs,
    };
  };

  // ── Edge-based: connections.inputs / connections.outputs ──
  // For Graph inputs: the SELECTED block is the consumer, so the
  // channel comes from its block-type. Every input row carries the
  // SAME channel (Expression reads all inputs as values; LogicGate
  // reads all as signals; etc.).
  // For Graph outputs: each row's referenced block IS the consumer,
  // so the channel comes from the row's target block-type. Different
  // downstreams may read different channels from the same producer.
  const selfChannel = getInputChannel(selected.type);
  const graphInputs = (selected.connections?.inputs ?? []).map(id =>
    resolve(String(id), '', selfChannel)
  );
  const graphOutputs = (selected.connections?.outputs ?? []).map(id => {
    const target = byId.get(String(id));
    const ch = target ? getInputChannel(target.type) : null;
    return resolve(String(id), '', ch);
  });

  // ── Runtime refs: id pointers on selected.data ──
  // Only the well-known runtime ref fields. We deliberately do NOT
  // walk arbitrary data.* fields — those could carry strings that
  // look like ids but aren't (e.g. user-typed labels).
  const data = (selected.data ?? {}) as Record<string, unknown>;
  const runtimeRefs: DependencyRef[] = [];
  for (const field of RUNTIME_REF_FIELDS_FOR(selected.type)) {
    const raw = data[field];
    if (typeof raw === 'string' && raw.length > 0) {
      runtimeRefs.push(resolve(raw, field));
    }
  }

  // ── Reverse scan: who references THIS block? ──
  // For every OTHER block in the strategy, check whether any of ITS
  // runtime-ref fields point at our id. We include the field name so
  // the UI can show "Long Entry · qtyCalculatorBlockId · ...7000d".
  const referencedBy: DependencyRef[] = [];
  for (const b of allBlocks) {
    if (b.id === selected.id) continue;
    const otherData = (b.data ?? {}) as Record<string, unknown>;
    for (const field of RUNTIME_REF_FIELDS_FOR(b.type)) {
      if (otherData[field] === selected.id) {
        referencedBy.push({
          refId: b.id,
          resolved: true,
          name: displayName(b),
          type: String(b.type),
          via: field,
          overrides: overridesFor(field),
          // Runtime refs don't use the BlockOutput channel system —
          // calc blocks are invoked per-trade with their own ctx,
          // not consumed from per-bar values/signals tracks.
          readsAs: null,
        });
      }
    }
  }

  return { graphInputs, graphOutputs, runtimeRefs, referencedBy };
}

/** S8f (2026-06-03) — Inline-field shadowing registry.
 *
 *  When a runtime-ref pointer is set + resolves, the engine reads
 *  ITS computed value instead of the inline data fields listed
 *  here. The Block Editor uses this map to:
 *    • Render an "overrides: …" suffix on the Dependencies panel row.
 *    • Show an explicit warning banner above the shadowed input
 *      group + disable the shadowed inputs so the user doesn't
 *      waste time tuning a value the engine will ignore.
 *
 *  Adding a new runtime-ref slot (e.g. for sl_calculator /
 *  tp_calculator when they ship) is a one-line addition here +
 *  the corresponding entry in RUNTIME_REF_FIELDS_FOR.
 */
const RUNTIME_REF_OVERRIDES: Readonly<Record<string, readonly string[]>> = Object.freeze({
  // Engine: src/engine/sizing.ts:390-428. When qtyCalculatorBlock is
  // present + evaluateQtyCalculator returns a finite number, the
  // method-switch path is skipped — so quantity_type / quantity_value
  // (and the legacy position_sizing_method / risk_per_trade fields
  // for the risk_based branch) become inert.
  qtyCalculatorBlockId: Object.freeze([
    'quantity_type',
    'quantity_value',
    'position_sizing_method',
    'risk_per_trade',
  ]),
  // Placeholders for the next two sibling slots. Engine integration
  // for these is task #38 (sl_calculator + tp_calculator: move
  // SL/TP math out of engine). Listing here is read-only forward-
  // looking metadata; the override banner only fires when the
  // pointer field is actually populated, so an empty calc block
  // ecosystem doesn't surface false override warnings.
  slCalculatorBlockId: Object.freeze([
    // (Filled in when sl_calculator dispatch lands; the engine reads
    // these from the STOP_LOSS terminal block, not the ENTRY, so
    // entries don't currently carry shadowable inline SL fields.)
  ]),
  tpCalculatorBlockId: Object.freeze([
    // (Filled in when tp_calculator dispatch lands; same story as
    // slCalculatorBlockId.)
  ]),
});

function overridesFor(via: string): readonly string[] {
  return RUNTIME_REF_OVERRIDES[via] ?? EMPTY;
}

/** S8f — public helper: does the given inline field of `block` get
 *  shadowed by an ACTIVE runtime-ref pointer? Returns the row that
 *  shadows it (so the UI can show the calc block's name) or null.
 *
 *  Active = pointer field is a non-empty string AND that id resolves
 *  to a real block in `allBlocks`. A stale pointer does NOT shadow
 *  the inline field — the engine will fall through to the method-
 *  switch path anyway, so the inline value DOES matter again.
 */
export function findShadowingRef(
  block: Block,
  field: string,
  allBlocks: Block[],
): DependencyRef | null {
  const deps = computeBlockDependencies(block, allBlocks);
  for (const r of deps.runtimeRefs) {
    if (r.resolved && r.overrides.includes(field)) return r;
  }
  return null;
}

/** Per-block-type set of `data.*` fields that carry block-id
 *  pointers. Centralized so tests can pin the exact list and so a
 *  future sibling field (e.g. risk_rule pointers, if added) lands
 *  in one place. */
function RUNTIME_REF_FIELDS_FOR(type: BlockType | string): readonly string[] {
  switch (type) {
    case BlockType.ENTRY:
      // TSL v1.2-pre §X — entries can delegate sizing + SL + TP to
      // custom blocks via these three id pointers. See
      // src/types/index.ts:607-612.
      return ENTRY_REF_FIELDS;
    case BlockType.EXPRESSION:
      // graphCompiler resolves operands via these (graph.ts:511-523).
      return EXPRESSION_REF_FIELDS;
    default:
      return EMPTY;
  }
}

const ENTRY_REF_FIELDS = Object.freeze([
  'qtyCalculatorBlockId',
  'slCalculatorBlockId',
  'tpCalculatorBlockId',
]);
const EXPRESSION_REF_FIELDS = Object.freeze([
  'leftInputBlockId',
  'rightInputBlockId',
]);
const EMPTY: readonly string[] = Object.freeze([]);

/** Best-effort display name. Most blocks carry `data.name`; some
 *  carry `data.title` (e.g. strategy_config); fall back to the
 *  block's type so the row is never blank. */
function displayName(b: Block): string {
  const d = (b.data ?? {}) as Record<string, unknown>;
  const candidates = [d.name, d.title, d.label];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c.trim();
  }
  return String(b.type);
}

/** Compact display string for a row, matching the user's spec:
 *  "<name> · <type> · ...<last8 of id>". For unresolved rows the
 *  caller renders this without the · separators or styles it muted —
 *  this helper just returns the canonical form. The `via` field name
 *  AND the override list (when present) are appended by the UI layer
 *  via formatDependencyRowFull — splitting the two keeps callers
 *  that only need the compact id label working as before. */
export function formatDependencyRow(r: DependencyRef): string {
  const tail = `...${r.refId.slice(-8)}`;
  if (!r.resolved) return `(unresolved) · ${tail}`;
  return `${r.name} · ${r.type} · ${tail}`;
}

/** S8f (2026-06-03) — extended display string used by the
 *  Dependencies panel. Adds the `via` field name + an
 *  "overrides: <field, field>" suffix when the row carries
 *  shadowing metadata. Callers that just want the compact id
 *  form keep using `formatDependencyRow`.
 *
 *  S8h (2026-06-03) — also appends the read-channel:
 *    " · reads as values"   (Expression)
 *    " · reads as signals"  (LogicGate / Entry / Exit / SL / TP)
 *    " · edge ignored"      (everything else — decorative)
 *  Suffix omitted for runtime-ref rows (readsAs is null because
 *  the channel system doesn't apply to per-trade dispatch).
 */
export function formatDependencyRowFull(r: DependencyRef): string {
  let line = formatDependencyRow(r);
  if (r.via) line += ` · ${r.via}`;
  if (r.overrides.length > 0) {
    line += ` · overrides: ${r.overrides.join(', ')}`;
  }
  // Channel suffix: only for graph-edge rows (via === '') where
  // we have a determinable channel. Runtime refs (via !== '')
  // are explicitly excluded — they don't use the BlockOutput
  // channel model.
  if (r.via === '') {
    if (r.readsAs === 'values') line += ' · reads as values';
    else if (r.readsAs === 'signals') line += ' · reads as signals';
    else line += ' · edge ignored';
  }
  return line;
}
